import { SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { ContestsDataTable } from "@/components/contests/contests.data-table";
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { contestsApi } from "@/services/contests/contests.api";
import { companiesApi } from "@/services/companies/companies.api";
import { $fetch } from "@/lib/fetch";
import {
  type ContestDto,
  type ContestFormDto,
  contestsDtoSchema,
  companiesDtoSchema,
} from "slde-api/dto-schemas";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ContestForm } from "@/components/contests/contest.form";

export default function ContestsPage() {
  const queryClient = useQueryClient();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [contestToUpdate, setContestToUpdate] = useState<ContestDto | null>(
    null,
  );

  const {
    data: { items },
  } = useSuspenseQuery({
    queryKey: contestsApi.contests.queryKey(),
    queryFn: async () =>
      await $fetch(contestsApi.contests.route(), {
        output: contestsDtoSchema,
        throw: true,
      }),
  });

  const {
    data: { items: companies },
  } = useSuspenseQuery({
    queryKey: companiesApi.companies.queryKey(),
    queryFn: async () =>
      await $fetch(companiesApi.companies.route(), {
        output: companiesDtoSchema,
        throw: true,
      }),
  });

  const { mutate: createContest, isPending: isCreatingContest } = useMutation({
    mutationFn: async (form: ContestFormDto) =>
      await $fetch(contestsApi.contests.route(), {
        method: "POST",
        body: form,
      }),
    onSuccess: () => {
      toast.success("Konkurs utworzono pomyślnie");
      setCreateDialogOpen(false);
      void queryClient.invalidateQueries();
    },
    onError: (e) => {
      if (e instanceof Error) {
        toast.error(`Wystąpił błąd przy dodawaniu konkursu: ${e.message}`);
      }
      console.error(e);
    },
  });

  const { mutate: updateContest, isPending: isUpdatingContest } = useMutation({
    mutationFn: async ({ id, ...form }: ContestFormDto & { id: number }) =>
      await $fetch(contestsApi.contest.route(id), {
        method: "PATCH",
        body: form,
      }),
    onSuccess: () => {
      toast.success("Zaktualizowano konkurs");
      setContestToUpdate(null);
      void queryClient.invalidateQueries();
    },
    onError: (e) => {
      if (e instanceof Error) {
        toast.error(`Wystąpił błąd przy aktualizacji konkursu: ${e.message}`);
      }
      console.error(e);
    },
  });

  const { mutate: deleteContest, isPending: isDeletingContest } = useMutation({
    mutationFn: async ({ id }: { id: number }) =>
      await $fetch(contestsApi.contest.route(id), {
        method: "DELETE",
      }),
    onSuccess: () => {
      toast.success("Konkurs został usunięty");
      void queryClient.invalidateQueries();
    },
    onError: (e) => {
      if (e instanceof Error) {
        toast.error(`Wystąpił błąd przy usuwaniu konkursu: ${e.message}`);
      }
      console.error(e);
    },
  });

  const isPending = isCreatingContest || isUpdatingContest || isDeletingContest;

  return (
    <div>
      <header className="flex h-(--header-height) shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-(--header-height)">
        <div className="flex w-full items-center gap-1 px-4 lg:gap-2 lg:px-6">
          <SidebarTrigger className="-ml-1" />
          <Separator
            orientation="vertical"
            className="mx-2 data-[orientation=vertical]:h-4"
          />
          <h1 className="text-base font-medium">Zarządzanie konkursami</h1>
          <div className="ml-auto flex items-center gap-2">
            <Button onClick={() => setCreateDialogOpen(true)}>
              Dodaj konkurs
            </Button>
          </div>
        </div>
      </header>

      <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
        <ContestsDataTable
          data={items}
          actions={{
            onUpdateClick: (contest) => setContestToUpdate(contest),
            onDeleteClick: (id) => deleteContest({ id }),
          }}
        />
      </div>

      <Dialog
        open={createDialogOpen || !!contestToUpdate}
        onOpenChange={(open) => {
          setCreateDialogOpen(open);
          setContestToUpdate(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {!!contestToUpdate ? "Edytuj konkurs" : "Dodaj konkurs"}
            </DialogTitle>
          </DialogHeader>

          <ContestForm
            companies={companies}
            onSubmit={(form) =>
              !!contestToUpdate
                ? updateContest({ id: contestToUpdate.id, ...form })
                : createContest(form)
            }
            defaultValues={
              contestToUpdate
                ? {
                    name: contestToUpdate.name,
                    description: contestToUpdate.description,
                    dueDate: contestToUpdate.dueDate,
                    linkUrl: contestToUpdate.linkUrl,
                    companyId: contestToUpdate.companyId,
                  }
                : undefined
            }
            disabled={isPending}
            loading={isPending}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
